import { JanusAPIv2Client } from '../client';
// Import log-specific types from their dedicated file
import { 
    QueryAdminLogsOptions, 
    ClientLogDto, 
    AdminLogsResponse 
} from '../types/logs.types'; 
// Import general types from the main types file
import { LogLevel, LogLevelInfo, LogsOptions, BaseRequestOptions } from '../types';
import { cleanUndefined } from '../utils';

// Helper to check if a string is a valid LogLevel
const validLogLevels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
const isValidLogLevel = (level: string): level is LogLevel => {
  return validLogLevels.includes(level as LogLevel);
};

export class LogsClient {
  constructor(private client: JanusAPIv2Client) {}

  /**
   * Query admin-viewable logs with filtering and pagination.
   * Requires admin privileges.
   * @param options Filtering and pagination options.
   * @returns A promise that resolves to the list of logs and pagination info.
   */
  async queryAdminLogs(options: QueryAdminLogsOptions = {}): Promise<AdminLogsResponse> {
    const cleanedOptions = cleanUndefined(options);
    return this.client._request<AdminLogsResponse>(
      'GET',
      'logs/admin',
      { params: cleanedOptions },
    );
  }

  /**
   * Get the available log levels configured on the backend.
   * Requires admin privileges.
   * @param options Request options.
   * @returns A promise that resolves to an array of valid LogLevel strings.
   */
  async getLogLevels(options?: LogsOptions): Promise<LogLevel[]> {
    const rawLevels = await this.client._request<string[]>('GET', 'logs/admin/config/levels', options);
    // Filter the received strings to ensure they are valid LogLevel types
    if (!Array.isArray(rawLevels)) {
        console.warn('Received invalid data type from /logs/admin/config/levels, expected array.');
        return [];
    }
    return rawLevels.filter(isValidLogLevel);
  }

  /**
   * Clear all admin-viewable logs.
   * Requires admin privileges.
   * This is a destructive operation.
   * @param options Request options.
   * @returns A promise that resolves to a success message.
   */
  async clearAdminLogs(options?: LogsOptions): Promise<{ message: string }> {
    return this.client._request<{ message: string }>('POST', 'logs/admin/clear', options);
  }

  /**
   * Submit a single client-side log to the backend.
   * No specific privileges required, but the backend might throttle or impose limits.
   * @param log The log object to submit.
   * @param options Request options.
   * @returns A promise that resolves when the request is complete (or rejects on error).
   */
  async submitClientLog(log: ClientLogDto, options?: BaseRequestOptions): Promise<void> {
    if (!log || typeof log !== 'object') {
      throw new Error('Log object is required');
    }
    if (!log.level || !log.message) {
      throw new Error('Log must have level and message properties');
    }
    
    await this.client._request<void>(
      'POST',
      'logs/client',
      { ...options, body: log },
    );
  }

  // Note: The client-side buffering logic (`log`, `sendClientLogs`, timers)
  // described in the design doc is managed within the main JanusAPIv2Client class,
  // not directly within this sub-client. This client primarily handles the API calls.
} 